import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Alert, Stack, TextField, Typography } from '@mui/material';
import { endpoints } from '../api/endpoints';
import { KeyValue, PageHeader, RelTime, SectionCard } from '../components/common';
import { LoadingButton } from '../components/LoadingButton';
import { useNotify } from '../components/Notify';
import { useAuth } from '../auth/AuthProvider';
import { TokensTab } from './Settings';

const MIN = 12;

function PasswordForm() {
  const notify = useNotify();
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [touched, setTouched] = useState(false);
  const err = next.length < MIN ? `At least ${MIN} characters` : next !== again ? 'The passwords differ' : null;
  const save = useMutation({
    mutationFn: () => endpoints.changePassword(cur, next),
    onSuccess: () => {
      notify.ok('Password changed. Other sessions were signed out.');
      setCur('');
      setNext('');
      setAgain('');
      setTouched(false);
    },
    onError: (e) => notify.error(e),
  });
  return (
    <Stack component="form" spacing={2} sx={{ maxWidth: 420 }} onSubmit={(e) => (e.preventDefault(), setTouched(true), !err && cur && save.mutate())}>
      <TextField type="password" label="Current password" autoComplete="current-password" value={cur} onChange={(e) => setCur(e.target.value)} />
      <TextField type="password" label="New password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} helperText={`At least ${MIN} characters`} />
      <TextField type="password" label="Repeat new password" autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} error={touched && !!err} helperText={touched && err ? err : ' '} />
      <LoadingButton type="submit" variant="contained" loading={save.isPending} sx={{ alignSelf: 'flex-start' }}>
        Change password
      </LoadingButton>
    </Stack>
  );
}

export function AccountPage() {
  const { user } = useAuth();
  return (
    <>
      <PageHeader title="Account" subtitle="Your sign-in and personal API tokens" />
      <Stack spacing={2}>
        <SectionCard title="You">
          <KeyValue
            items={[
              ['E-mail', user?.email],
              ['Role', <span key="r" style={{ textTransform: 'capitalize' }}>{user?.role}</span>],
              ['2FA', user?.totpEnabled ? 'on' : 'off'],
              ['Last sign-in', <RelTime key="l" iso={user?.lastLoginAt} />],
            ]}
          />
        </SectionCard>
        <SectionCard title="Password">
          <PasswordForm />
        </SectionCard>
        <SectionCard title="API tokens">
          <TokensTab />
        </SectionCard>
        <Alert severity="info" icon={false}>
          <Typography variant="body2">Lost the authenticator? Ask an owner to reset your 2FA in Settings → Users.</Typography>
        </Alert>
      </Stack>
    </>
  );
}
